import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { ShoppingBagIcon } from '@heroicons/react/outline';
import { Modal } from 'components/Modal';
import { GET_TRACKING_AD_PRODUCTS } from 'gql/queries';

/**
 * Picks a Shopify product to advertise on the tracking page.
 *
 * The selection is copied into the merchant's config as a *snapshot* rather than
 * stored as a product reference, so the public tracking page never calls Shopify
 * while rendering. The trade-off is staleness, which the ad's refresh control
 * addresses.
 */

export type ProductSnapshot = {
  productId: string;
  imageUrl: string;
  headline: string;
  body: string | null;
  ctaUrl: string;
};

type Product = {
  id: string;
  title: string;
  description: string | null;
  imageUrl: string;
  url: string;
};

export const toSnapshot = (product: Product): ProductSnapshot => ({
  productId: product.id,
  imageUrl: product.imageUrl,
  headline: product.title,
  body: product.description,
  ctaUrl: product.url,
});

export const ShopifyProductPicker = ({
  show,
  storeId,
  onSelect,
  onClose,
}: {
  show: boolean;
  storeId: string;
  onSelect: (snapshot: ProductSnapshot) => void;
  onClose: () => void;
}) => {
  const [term, setTerm] = useState('');
  const [search, { data, loading, error }] = useLazyQuery(GET_TRACKING_AD_PRODUCTS, {
    fetchPolicy: 'network-only',
  });

  const runSearch = (value: string) =>
    search({ variables: { input: { storeId, search: value } } });

  // Load the catalogue as soon as the modal opens, so the merchant browses
  // rather than having to guess a search term first.
  useEffect(() => {
    if (show && storeId) runSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, storeId]);

  const products: Product[] = data?.trackingAdProducts ?? [];

  if (!show) return null;

  return (
    <Modal
      open={show}
      openModal={() => onClose()}
      containerClass="w-full max-w-3xl !p-0 overflow-hidden"
      iconClassName="text-[#64748b] hover:text-[#0b1c30]"
    >
      <div className="px-6 pb-6 pt-1">
        <div className="mb-5 flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef5ff] text-[#2563eb]">
            <ShoppingBagIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#0b1c30]">
              Choose a product
            </h2>
            <p className="text-sm text-[#6d7175]">
              Pick a product from your Shopify store. Its title, image and link
              are copied into the promotion.
            </p>
          </div>
        </div>

        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            runSearch(term);
          }}
          className="flex gap-2 mb-4"
        >
          <input
            type="text"
            aria-label="Search products"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search by title…"
            className="border border-gray-300 rounded-md text-sm py-2 px-3 flex-1"
          />
          <button
            type="submit"
            className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50"
          >
            Search
          </button>
        </form>

        {error ? (
          <p role="alert" className="text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">
            {error.message}
          </p>
        ) : null}

        {loading ? (
          <p className="text-sm text-[#6d7175] py-8 text-center">
            Loading products…
          </p>
        ) : null}

        {!loading && !error && products.length === 0 ? (
          <p className="text-sm text-[#6d7175] py-8 text-center">
            No products found. Try a different search term.
          </p>
        ) : null}

        {!loading && products.length > 0 ? (
          <>
            <p className="text-xs uppercase tracking-wider text-[#6d7175] mb-2">
              {`${products.length} product${products.length === 1 ? '' : 's'}`}
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto pr-1">
              {products.map((product) => (
                <li key={product.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(toSnapshot(product));
                      onClose();
                    }}
                    className="w-full h-full flex items-center gap-3 text-left p-2 rounded-md bg-white border border-gray-200 hover:border-blue-400 hover:bg-[#f9fbff]"
                  >
                    <img
                      src={product.imageUrl}
                      alt=""
                      className="w-14 h-14 object-cover rounded shrink-0 bg-gray-50"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-[#0b1c30] truncate">
                        {product.title}
                      </span>
                      {product.description ? (
                        <span className="block text-xs text-[#6d7175] line-clamp-2">
                          {product.description}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default ShopifyProductPicker;
